import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getAssessment, submitAssessment } from "@/lib/companyApi";
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import ScoreRing from "@/components/wishire/ScoreRing";

export default function TakeAssessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAssessment(id);
        setAssessment(data);
      } catch (e) {
        setError("Assessment not found");
      }
    })();
  }, [id]);

  const setAns = (i, val) => setAnswers((a) => ({ ...a, [i]: val }));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = assessment.questions.map((_, i) => ({
        index: i,
        value: String(answers[i] ?? ""),
      }));
      const { data } = await submitAssessment(id, payload);
      setResult(data.submission);
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <div className="container-x py-10 text-red-700">{error}</div>;
  if (!assessment) return <div className="container-x py-10 text-stone-500">Loading…</div>;

  if (result) {
    return (
      <div className="container-x py-10 md:py-14 max-w-3xl">
        <div className="rounded-3xl bg-stone-900 text-stone-50 p-8 md:p-12 wsh-grain relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-40"
            style={{ background: "radial-gradient(500px 300px at 80% 20%, rgba(217,119,6,0.35), transparent 60%)" }}
          />
          <div className="relative">
            <span className="overline" style={{ color: "rgba(253,251,247,0.6)" }}>Assessment submitted</span>
            <h1 className="font-display text-4xl mt-3 mb-6">
              You scored <span className="text-amber-300" data-testid="ta-total-score">{result.total_score}</span>/100
            </h1>
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <ScoreRing value={result.total_score} size={200} dark />
              <div>
                <p className="overline" style={{ color: "rgba(253,251,247,0.6)" }}>{result.grade?.verdict}</p>
                <p className="mt-2 text-stone-200">{result.grade?.summary}</p>
              </div>
            </div>
            <div className="mt-8 space-y-3">
              {(result.grade?.per_question || []).map((pq, i) => (
                <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-3">
                  <p className="text-sm">
                    <span className="font-mono text-amber-300">Q{pq.index + 1}</span> · <span className="text-stone-100">{pq.score}/100</span>
                  </p>
                  <p className="text-stone-300 text-sm mt-1">{pq.feedback}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button onClick={() => navigate("/app")} className="btn-primary">
            Back to dashboard <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-10 md:py-14 max-w-3xl">
      <Link to="/app" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6">
        <ArrowLeft size={14} /> Dashboard
      </Link>
      <span className="overline">Assessment · {assessment.duration_min} min</span>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mt-3 mb-8" data-testid="ta-title">
        {assessment.title}
      </h1>

      <ol className="space-y-6">
        {assessment.questions.map((q, i) => (
          <li key={i} className="rounded-3xl bg-white border border-stone-200 p-6" data-testid={`ta-q-${i}`}>
            <p className="overline mb-2">Question {i + 1} · {q.type}</p>
            <p className="text-stone-900 font-display text-xl leading-snug mb-4">{q.prompt}</p>

            {q.type === "mcq" && (
              <div className="space-y-2">
                {(q.options || []).map((o, j) => (
                  <label
                    key={j}
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer transition-colors ${
                      answers[i] === o
                        ? "border-stone-900 bg-stone-50"
                        : "border-stone-300 hover:border-stone-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q-${i}`}
                      checked={answers[i] === o}
                      onChange={() => setAns(i, o)}
                      data-testid={`ta-q-${i}-opt-${j}`}
                    />
                    <span>{o}</span>
                  </label>
                ))}
              </div>
            )}

            {q.type === "short" && (
              <textarea
                rows={3}
                value={answers[i] || ""}
                onChange={(e) => setAns(i, e.target.value)}
                placeholder="Your answer…"
                className="w-full px-4 py-3 rounded-2xl bg-stone-50 border border-stone-300 focus:outline-none focus:border-stone-900 text-sm"
                data-testid={`ta-q-${i}-input`}
              />
            )}

            {q.type === "coding" && (
              <>
                <p className="overline mb-2">Language: {q.language || "any"}</p>
                <textarea
                  rows={10}
                  value={answers[i] || ""}
                  onChange={(e) => setAns(i, e.target.value)}
                  placeholder="// Write your solution here"
                  className="w-full px-4 py-3 rounded-2xl bg-stone-950 text-stone-50 border border-stone-800 focus:outline-none focus:border-amber-300 font-mono text-sm"
                  data-testid={`ta-q-${i}-input`}
                />
              </>
            )}
          </li>
        ))}
      </ol>

      {error && <p className="text-red-700 text-sm mt-4">{error}</p>}

      <div className="mt-8">
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="ta-submit"
          className="btn-primary disabled:opacity-60"
        >
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Grading your answers…</> : <>Submit & get graded <CheckCircle2 size={16} /></>}
        </button>
      </div>
    </div>
  );
}
